class SavannahHomeTabs extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;
    this.abortController = new AbortController();
    this.buttons = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    this.buttons.forEach((button, index) => {
      button.addEventListener('click', () => this.select(index), { signal: this.abortController.signal });
      button.addEventListener('keydown', (event) => this.onKeydown(event, index), { signal: this.abortController.signal });
    });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = undefined;
  }

  select(index) {
    this.buttons.forEach((button, buttonIndex) => {
      const selected = buttonIndex === index;
      button.setAttribute('aria-selected', String(selected));
      button.tabIndex = selected ? 0 : -1;
      this.panels[buttonIndex].hidden = !selected;
    });
  }

  onKeydown(event, index) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    let nextIndex = index;
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % this.buttons.length;
    if (event.key === 'ArrowLeft') nextIndex = (index - 1 + this.buttons.length) % this.buttons.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.buttons.length - 1;
    this.select(nextIndex);
    this.buttons[nextIndex].focus();
  }
}

if (!customElements.get('savannah-home-tabs')) {
  customElements.define('savannah-home-tabs', SavannahHomeTabs);
}

class SavannahHomeVariants extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;
    this.abortController = new AbortController();
    this.chips = Array.from(this.querySelectorAll('[role="radio"][data-sbh-variant-id]'));
    this.chips.forEach((chip, index) => {
      chip.addEventListener('click', () => this.select(index), { signal: this.abortController.signal });
      chip.addEventListener('keydown', (event) => this.onKeydown(event, index), {
        signal: this.abortController.signal
      });
    });
    this.dataset.sbhVariantReady = 'true';
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = undefined;
    delete this.dataset.sbhVariantReady;
  }

  select(index, focus = false) {
    const selectedChip = this.chips[index];
    if (!selectedChip) return;

    this.chips.forEach((chip, chipIndex) => {
      const selected = chipIndex === index;
      chip.setAttribute('aria-checked', String(selected));
      chip.tabIndex = selected ? 0 : -1;
    });

    const productForm = this.closest('product-form-component');
    const productCard = this.closest('.sbh-product-card');
    const variantId = productForm?.querySelector('[ref="variantId"]');
    const price = productCard?.querySelector('[data-sbh-variant-price]');
    const image = productCard?.querySelector('.sbh-product-card__image');
    const liveRegion = productForm?.querySelector('[ref="liveRegion"]');

    if (variantId instanceof HTMLInputElement) {
      variantId.value = selectedChip.dataset.sbhVariantId || '';
      variantId.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (price && selectedChip.dataset.sbhVariantPrice) {
      price.textContent = selectedChip.dataset.sbhVariantPrice;
    }
    if (image instanceof HTMLImageElement && selectedChip.dataset.sbhVariantImageSrc) {
      image.src = selectedChip.dataset.sbhVariantImageSrc;
      image.srcset = selectedChip.dataset.sbhVariantImageSrcset || '';
      image.alt = selectedChip.dataset.sbhVariantImageAlt || image.alt;
    }
    if (liveRegion) {
      const productTitle = productCard?.querySelector('h3')?.textContent?.trim() || '';
      const variantLabel = selectedChip.textContent?.trim() || '';
      const variantPrice = selectedChip.dataset.sbhVariantPrice || '';
      liveRegion.textContent = [productTitle, variantLabel, variantPrice].filter(Boolean).join(', ');
    }
    if (focus) selectedChip.focus();
  }

  onKeydown(event, index) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    let nextIndex = index;
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % this.chips.length;
    if (event.key === 'ArrowLeft') nextIndex = (index - 1 + this.chips.length) % this.chips.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.chips.length - 1;
    this.select(nextIndex, true);
  }
}

if (!customElements.get('savannah-home-variants')) {
  customElements.define('savannah-home-variants', SavannahHomeVariants);
}

function wireSavannahHomeRails(root = document) {
  root.querySelectorAll('.sbh-home').forEach((section) => {
    if (section.dataset.sbhRailsReady === 'true') return;
    const rail = section.querySelector('[data-sbh-rail]');
    if (!rail) return;
    const previous = section.querySelector('[data-sbh-rail-prev]');
    const next = section.querySelector('[data-sbh-rail-next]');
    const slides = Array.from(rail.querySelectorAll('[data-sbh-rail-slide]'));
    const dots = Array.from(section.querySelectorAll('[data-sbh-rail-dot]'));

    if (slides.length > 0 && dots.length === slides.length) {
      const step = () => slides[1] ? slides[1].offsetLeft - slides[0].offsetLeft : rail.clientWidth;
      const selectedIndex = () => Math.max(0, Math.min(slides.length - 1, Math.round(rail.scrollLeft / step())));
      const update = () => {
        const index = selectedIndex();
        dots.forEach((dot, dotIndex) => dot.setAttribute('aria-current', String(dotIndex === index)));
        if (previous) previous.disabled = index === 0;
        if (next) next.disabled = index === slides.length - 1;
      };
      const select = (index) => {
        const boundedIndex = Math.max(0, Math.min(slides.length - 1, index));
        rail.scrollTo({ left: boundedIndex * step(), behavior: 'smooth' });
      };
      previous?.addEventListener('click', () => select(selectedIndex() - 1));
      next?.addEventListener('click', () => select(selectedIndex() + 1));
      dots.forEach((dot) => dot.addEventListener('click', () => select(Number(dot.dataset.sbhRailIndex))));
      let scheduled = false;
      rail.addEventListener('scroll', () => {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => {
          update();
          scheduled = false;
        });
      }, { passive: true });
      update();
    } else {
      previous?.addEventListener('click', () => rail.scrollBy({ left: -rail.clientWidth * 0.82, behavior: 'smooth' }));
      next?.addEventListener('click', () => rail.scrollBy({ left: rail.clientWidth * 0.82, behavior: 'smooth' }));
    }
    section.dataset.sbhRailsReady = 'true';
  });
}

document.addEventListener('DOMContentLoaded', () => wireSavannahHomeRails());
document.addEventListener('shopify:section:load', (event) => wireSavannahHomeRails(event.target));
